// dashboard/frontend/src/components/Auth/Register.js
import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    TextField,
    Button,
    Typography,
    Box,
    Alert,
    CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const Register = () => {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [paymentInfo, setPaymentInfo] = useState(null);
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';

    // Simplified form state
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        confirmPassword: '',
    });

    useEffect(() => {
        const pendingReg = localStorage.getItem('pendingRegistration');
        if (!pendingReg) {
            navigate('/');
            return;
        }
        
        const parsedInfo = JSON.parse(pendingReg);
        setPaymentInfo(parsedInfo);
    }, [navigate]);    

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            // Get payment info from localStorage
            const pendingReg = localStorage.getItem('pendingRegistration');
            const paymentInfo = pendingReg ? JSON.parse(pendingReg) : null;

            // Validate passwords match
            if (formData.password !== formData.confirmPassword) {
                setError('Passwords do not match');
                setLoading(false);
                return;
            }

            // Prepare registration data - combine username/password with stored customer info
            const registrationData = {
                username: formData.username,
                password: formData.password,
                // Include stored customer info from payment step
                email: paymentInfo.customerInfo.email,
                first_name: paymentInfo.customerInfo.firstName,
                last_name: paymentInfo.customerInfo.lastName,
                company_name: paymentInfo.customerInfo.companyName,
                payment_id: paymentInfo.paymentId,
                plan: paymentInfo.plan,
                timezone: browserTimezone
            };

            // Call registration API
            const response = await fetch('/api/auth/register/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(registrationData)
            });

            const data = await response.json();

            if (response.ok) {
                // Clear payment info
                localStorage.removeItem('pendingRegistration');
                // Redirect to dashboard
                navigate('/dashboard');
            } else {
                setError(data.message || 'Registration failed');
            }
        } catch (error) {
            console.error('Registration error:', error);
            setError('An error occurred during registration');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    return (
        <Container maxWidth="sm">
            <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
                <Typography variant="h4" gutterBottom>
                    Create Your Account
                </Typography>

                {paymentInfo && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        Payment successful! Complete your registration for the {paymentInfo.plan} plan.
                    </Alert>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <form onSubmit={handleSubmit}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Username"
                            name="username"
                            value={formData.username}
                            onChange={handleChange}
                            required
                            autoFocus
                        />
                        <TextField
                            label="Password"
                            name="password"
                            type="password"
                            value={formData.password}
                            onChange={handleChange}
                            required
                        />
                        <TextField
                            label="Confirm Password"
                            name="confirmPassword"
                            type="password"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            required
                        />

                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={loading}
                            sx={{ mt: 2 }}
                        >
                            {loading ? <CircularProgress size={24} /> : 'Complete Registration'}
                        </Button>
                    </Box>
                </form>
            </Paper>
        </Container>
    );
};

export default Register;